import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Crown, Users, Globe, Lock, Plus, ChevronRight } from 'lucide-react';

/**
 * MY GROUPS QUICK-NAV
 * Lets a user jump straight to a group they own without scanning the full
 * groups grid. Two layouts sharing one data source:
 *   - "sidebar": vertical card list (GroupListPage's right column)
 *   - "strip":   horizontal scrollable row (Dashboard top, since its own
 *                grid layout is conditional on watchlist state and this
 *                needs to always be reachable)
 */

const isOwnedBy = (group, userId) =>
  !!userId && (group?.created_by === userId || group?.createdBy?.id === userId);

const groupInitials = (name = '') =>
  name.trim().slice(0, 2).toUpperCase() || 'GR';

const GroupAvatar = ({ group, size = 'w-10 h-10', textSize = 'text-sm' }) => (
  <div className={`${size} rounded-xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm`}>
    <span className={`${textSize} font-bold text-white`}>{groupInitials(group?.name)}</span>
  </div>
);

const SidebarRow = ({ group }) => {
  const { t } = useTranslation();
  const memberCount = Array.isArray(group?.members) ? group.members.length : 0;
  return (
    <Link
      to={`/groups/${group.id}`}
      className="flex items-center gap-3 p-3 rounded-xl hover:bg-cream-900/5 dark:hover:bg-gray-700/40 transition-colors group/row"
    >
      <GroupAvatar group={group} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{group?.name}</span>
          {group?.privacy === 'public'
            ? <Globe className="w-3 h-3 text-green-600 dark:text-green-400 flex-shrink-0" />
            : <Lock className="w-3 h-3 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />}
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
          <Users className="w-3 h-3" />
          <span>{t('groups.myGroupsNav.memberCountLabel', { count: memberCount })}</span>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover/row:text-gray-600 dark:group-hover/row:text-gray-300 transition-colors flex-shrink-0" />
    </Link>
  );
};

const SidebarVariant = ({ owned }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-cream-100 dark:bg-gray-800/50 rounded-2xl border border-cream-300 dark:border-gray-700/50 overflow-hidden lg:sticky lg:top-6">
      <div className="p-4 border-b border-cream-300 dark:border-gray-700/50 flex items-center justify-between">
        <h3 className="text-gray-900 dark:text-white font-bold flex items-center gap-2">
          <Crown className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
          {t('groups.myGroupsNav.yourGroups')}
        </h3>
        {owned.length > 0 && (
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-cream-200 dark:bg-gray-900/50 px-2 py-1 rounded-full">
            {owned.length}
          </span>
        )}
      </div>

      {owned.length === 0 ? (
        <div className="p-6 text-center">
          <Crown className="w-10 h-10 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('groups.myGroupsNav.noOwnedGroups')}
          </p>
          <Link
            to="/create-group"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-500 to-blue-600 hover:from-violet-400 hover:to-blue-500 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors shadow-sm shadow-violet-500/20"
          >
            <Plus className="w-4 h-4" />
            {t('groups.myGroupsNav.createAGroup')}
          </Link>
        </div>
      ) : (
        <>
          <div className="p-2 max-h-[420px] overflow-y-auto">
            {owned.map((g) => (
              <SidebarRow key={g.id} group={g} />
            ))}
          </div>
          <Link
            to="/create-group"
            className="flex items-center justify-center gap-2 p-3 border-t border-cream-300 dark:border-gray-700/50 text-sm text-blue-700 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 hover:bg-cream-900/5 dark:hover:bg-gray-700/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('groups.myGroupsNav.createAnotherGroup')}
          </Link>
        </>
      )}
    </div>
  );
};

const StripChip = ({ group }) => {
  const memberCount = Array.isArray(group?.members) ? group.members.length : 0;
  return (
    <Link
      to={`/groups/${group.id}`}
      className="flex items-center gap-2 flex-shrink-0 bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700/60 hover:border-violet-400 dark:hover:border-violet-500/60 rounded-full pl-1.5 pr-3 py-1.5 transition-colors"
    >
      <GroupAvatar group={group} size="w-6 h-6" textSize="text-[9px]" />
      <span className="text-xs font-medium text-gray-800 dark:text-slate-200 whitespace-nowrap max-w-[140px] truncate">
        {group?.name}
      </span>
      <span className="text-[10px] text-gray-400 dark:text-slate-500 whitespace-nowrap">
        {memberCount}
      </span>
    </Link>
  );
};

const StripVariant = ({ owned }) => {
  const { t } = useTranslation();
  if (owned.length === 0) return null;

  return (
    <div className="mb-4 sm:mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Crown className="w-4 h-4 text-yellow-500" />
        <h2 className="text-xs sm:text-sm font-bold text-gray-800 dark:text-slate-200">
          {t('groups.myGroupsNav.yourGroups')}
        </h2>
      </div>
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-none pb-1">
        {owned.map((g) => (
          <StripChip key={g.id} group={g} />
        ))}
        <Link
          to="/groupies"
          className="flex items-center gap-1 flex-shrink-0 text-xs font-medium text-blue-700 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 pl-2 pr-3 py-1.5 whitespace-nowrap"
        >
          {t('groups.myGroupsNav.viewAll')}
          <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

const MyGroupsNav = ({ groups = [], currentUser, variant = 'sidebar' }) => {
  const owned = useMemo(() => {
    if (!Array.isArray(groups)) return [];
    return groups.filter((g) => isOwnedBy(g, currentUser?.id));
  }, [groups, currentUser]);

  return variant === 'strip'
    ? <StripVariant owned={owned} />
    : <SidebarVariant owned={owned} />;
};

export default MyGroupsNav;
